/** Client mirror of transport source enablement labels (M1 registry audit). */

export const TRANSPORT_ENABLEMENT_STATUS_LABELS = {
  healthy_enabled: "Enabled and healthy",
  enabled_not_configured: "Enabled but not configured",
  enabled_stub_only: "Enabled — stub only",
  enabled_stale: "Enabled but stale",
  enabled_degraded: "Enabled but degraded",
  enabled_pending_review: "Enabled — pending license review",
  disabled_ready_to_enable: "Disabled — ready to enable",
  disabled_discovery_pending: "Disabled — endpoint discovery pending",
  disabled_license_required: "Disabled — license review required",
  disabled_legal_required: "Disabled — legal review required",
  disabled_product_decision: "Disabled — product decision required",
  disabled_third_party_hold: "Disabled — third-party hold",
  disabled_future: "Disabled — future milestone",
  unknown: "Unknown",
};

export const TRANSPORT_SOURCE_GROUP_LABELS = {
  core_network: "Core network",
  ridership_intelligence: "Ridership & intelligence",
  community_manual: "Community & manual",
  roads_regional: "Roads & regional",
  metro_live_ops: "Metro live operations",
  bus_live_ops: "Bus live operations",
  official_alerts: "Official alerts & notices",
  routing: "Routing providers",
  third_party: "Third-party candidates",
  unknown: "Unknown",
  transport_registry: "Transport registry",
};

export const TRANSPORT_READINESS_TIER_LABELS = {
  tier_0: "Tier 0 — Map foundation",
  tier_1: "Tier 1 — Intelligence",
  tier_2: "Tier 2 — Operations & community",
  tier_3: "Tier 3 — Discovery / live / future",
};

export function transportEnablementStatusLabel(status) {
  return TRANSPORT_ENABLEMENT_STATUS_LABELS[status] || status || "Unknown";
}

export function transportOperationalGroupLabel(group) {
  return TRANSPORT_SOURCE_GROUP_LABELS[group] || group || null;
}

export function transportReadinessTierLabel(tier) {
  return TRANSPORT_READINESS_TIER_LABELS[tier] || tier || null;
}

/** Visual tone for StatusBadge — discovery/future are neutral, not failures. */
export function transportEnablementTone(status) {
  if (status === "healthy_enabled") return "confirmed";
  if (
    status === "disabled_discovery_pending" ||
    status === "disabled_future"
  ) {
    return "neutral";
  }
  if (
    status === "disabled_third_party_hold" ||
    status === "disabled_legal_required" ||
    status === "disabled_license_required"
  ) {
    return "disputed";
  }
  if (status === "disabled_ready_to_enable") return "medium";
  if (String(status || "").startsWith("enabled_")) return "medium";
  if (String(status || "").startsWith("disabled_")) return "neutral";
  return "neutral";
}

export const TRANSPORT_ENABLEMENT_FILTER_OPTIONS = [
  { value: "all", label: "All enablement" },
  { value: "enabled", label: "Registry enabled" },
  { value: "disabled", label: "Registry disabled" },
  { value: "healthy_enabled", label: "Healthy enabled" },
  { value: "not_configured", label: "Not configured" },
  { value: "stub_only", label: "Stub only" },
  { value: "stale_degraded", label: "Stale or degraded" },
  { value: "pending_review", label: "Pending review" },
  { value: "discovery_pending", label: "Discovery pending" },
  { value: "third_party_hold", label: "Third-party hold" },
  { value: "legal_required", label: "Legal / license required" },
  { value: "ready_to_enable", label: "Ready to enable" },
  { value: "tier_0", label: "Tier 0" },
  { value: "tier_1", label: "Tier 1" },
];

export function matchesTransportEnablementFilter(source, filter) {
  if (!filter || filter === "all") return true;
  if (filter === "enabled") return Boolean(source.isActive ?? source.isEnabled);
  if (filter === "disabled") return !(source.isActive ?? source.isEnabled);
  if (filter === "tier_0") return source.readinessTier === "tier_0" || source.transportMetadata?.readinessTier === "tier_0";
  if (filter === "tier_1") return source.readinessTier === "tier_1" || source.transportMetadata?.readinessTier === "tier_1";
  if (filter === "not_configured") {
    return (
      source.enablementStatus === "enabled_not_configured" ||
      source.transportMetadata?.enablementStatus === "enabled_not_configured"
    );
  }
  if (filter === "stub_only") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return status === "enabled_stub_only" || source.healthStatus === "stub_only";
  }
  if (filter === "stale_degraded") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return status === "enabled_stale" || status === "enabled_degraded";
  }
  if (filter === "pending_review") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return status === "enabled_pending_review";
  }
  if (filter === "discovery_pending") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return (
      status === "disabled_discovery_pending" ||
      source.healthStatus === "discovery_pending"
    );
  }
  if (filter === "third_party_hold") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return status === "disabled_third_party_hold";
  }
  if (filter === "legal_required") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return (
      status === "disabled_legal_required" ||
      status === "disabled_license_required"
    );
  }
  if (filter === "ready_to_enable") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return status === "disabled_ready_to_enable";
  }
  if (filter === "healthy_enabled") {
    const status = source.enablementStatus || source.transportMetadata?.enablementStatus;
    return status === "healthy_enabled";
  }
  return true;
}
